import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Grid
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";
import Loader from "../../components/loader/loader";
import CustomButton from "../../components/CommonButton/CommonButton";
import { getErrorMessage } from "../../Utils/getErrorMessage";

interface DebitCardProps {
  businessData?: any;
  businessToken?: string | null;
  paymentDetails?: any;
  returnUrl?: string;
}

interface DebitCardFormData {
  AccountNumber: string;
  AccountBSB: string;
  AccountName: string;
  FirstName: string;
  LastName: string;
  Address: string;
  Suburb: string;
  Postcode: string;
  Email: string;
  Mobile: string;
  State: string;
}

const DebitCard: React.FC<DebitCardProps> = ({
  businessData: businessDataProp,
  businessToken: businessTokenProp,
  paymentDetails: paymentDetailsProp,
  returnUrl: returnUrlProp
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState<DebitCardFormData>({
    AccountNumber: "",
    AccountBSB: "",
    AccountName: "",
    FirstName: "",
    LastName: "",
    Address: "",
    Suburb: "",
    Postcode: "",
    Email: "",
    Mobile: "",
    State: ""
  });
  const [validationErrors, setValidationErrors] = useState<
    Partial<DebitCardFormData>
  >({});

  // Get business details from localStorage
  const [businessData, setBusinessData] = useState<any>(
    businessDataProp || null
  );
  const [businessToken, setBusinessToken] = useState<string | null>(
    businessTokenProp || null
  );
  const [paymentDetails, setPaymentDetails] = useState<any>(
    paymentDetailsProp || null
  );

  // Get return URL from location state
  const returnUrl =
    returnUrlProp || location.state?.returnUrl || "/account/paymentdetails";

  // Initialize business data from localStorage
  useEffect(() => {
    if (!businessDataProp || !businessTokenProp) {
      const currentBusiness = localStorage.getItem("currentBusiness");
      const token = localStorage.getItem("businessAccessToken");
      if (currentBusiness && !businessData) {
        setBusinessData(JSON.parse(currentBusiness));
      }
      if (!businessToken) {
        setBusinessToken(token);
      }
    }
  }, [businessDataProp, businessTokenProp, businessData, businessToken]);

  useEffect(() => {
    if (businessData.BusinessId) {
      setFormData((prev) => ({
        ...prev,
        AccountName: "",
        Address: businessData.StreetNumber
          ? businessData.StreetNumber
          : "" + " " + businessData.Street
          ? businessData.Street
          : "",
        Suburb: businessData.Suburb ? businessData.Suburb : "",
        Postcode: businessDataProp?.PostalCode ? businessData.PostalCode : "",
        State: businessData.State ? businessData.State : "",
        Email: businessData.Email ? businessData.Email : "",
        Mobile: businessData.PhoneNumber ? businessData.PhoneNumber : ""
      }));
    }
  }, [businessDataProp]);

  // Fetch existing payment details
  const fetchExistingPaymentDetails = useCallback(async () => {
    if (!businessData?.BusinessId || !businessToken) return;

    setIsInitialLoading(true);
    try {
      // If details passed in props, use them; else fetch
      let details = paymentDetails;
      if (!details) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/billingplatform-business/billingdetails/${businessData.BusinessId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(
                "businessAccessToken"
              )}`,
              "Content-Type": "application/json"
            }
          }
        );
        details = response.data;
        setPaymentDetails(details);
      }

      if (details) {
        const existingData = details;
        if (
          existingData.AccountNumber ||
          existingData.AccountBSB ||
          existingData.AccountName
        ) {
          setFormData((prev) => ({
            ...prev,
            AccountNumber: existingData.AccountNumber || "",
            AccountBSB: existingData.AccountBSB || "",
            AccountName: existingData.AccountName || ""
          }));
        }
      }
    } catch (error: any) {
      console.log("No existing payment details found or error:", error);
      // This is not necessarily an error - the business might not have payment details yet
    } finally {
      setIsInitialLoading(false);
    }
  }, [businessData?.BusinessId, businessToken, paymentDetails]);

  // Fetch existing payment details when business data is available
  useEffect(() => {
    if (businessData?.BusinessId && businessToken) {
      fetchExistingPaymentDetails();
    }
  }, [businessData?.BusinessId, businessToken, fetchExistingPaymentDetails]);

  const validateField = (field: keyof DebitCardFormData, value: string) => {
    let errorMessage = "";

    switch (field) {
      case "AccountName":
        if (!value.trim()) {
          errorMessage = "Account name is required";
        } else if (value.trim().length < 2) {
          errorMessage = "Account name must be at least 2 characters";
        }
        break;
      case "AccountBSB":
        if (!value.trim()) {
          errorMessage = "BSB is required";
        } else if (!/^\d{3}-?\d{3}$/.test(value.trim())) {
          errorMessage = "BSB must be 6 digits (format: 123-456 or 123456)";
        }
        break;
      case "AccountNumber":
        if (!value.trim()) {
          errorMessage = "Account number is required";
        } else if (!/^\d{6,10}$/.test(value.trim())) {
          errorMessage = "Account number must be between 6-10 digits";
        }
        break;
      case "FirstName":
        if (!value.trim()) {
          errorMessage = "First name is required";
        } else if (value.trim().length < 2) {
          errorMessage = "First name must be at least 2 characters";
        }
        break;
      case "LastName":
        if (!value.trim()) {
          errorMessage = "Last name is required";
        } else if (value.trim().length < 2) {
          errorMessage = "Last name must be at least 2 characters";
        }
        break;
      case "Address":
        if (!value.trim()) {
          errorMessage = "Address is required";
        }
        break;
      case "Suburb":
        if (!value.trim()) {
          errorMessage = "Suburb is required";
        }
        break;
      case "Postcode":
        if (value.trim() && !/^\d{4}$/.test(value.trim())) {
          errorMessage = "Postcode must be 4 digits";
        }
        break;
      case "Email":
        if (!value.trim()) {
          errorMessage = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          errorMessage = "Please enter a valid email address";
        }
        break;
      case "Mobile":
        if (value.trim() && !/^[\d\s\-+()]{10,15}$/.test(value.trim())) {
          errorMessage = "Please enter a valid mobile number";
        }
        break;
      case "State":
        if (!value.trim()) {
          errorMessage = "State is required";
        }
        break;
    }

    setValidationErrors((prev) => ({
      ...prev,
      [field]: errorMessage
    }));

    return !errorMessage;
  };

  const handleInputChange = (field: keyof DebitCardFormData, value: string) => {
    let processedValue = value;

    // Auto-format BSB with hyphen
    if (field === "AccountBSB") {
      // Remove any existing hyphens
      const digitsOnly = value.replace(/-/g, "");
      // Add hyphen after 3rd digit if we have more than 3 digits
      if (digitsOnly.length > 3) {
        processedValue = digitsOnly.slice(0, 3) + "-" + digitsOnly.slice(3, 6);
      } else {
        processedValue = digitsOnly;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: processedValue
    }));
    validateField(field, processedValue);
  };

  const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      (charCode < 48 || charCode > 57) &&
      charCode !== 8 &&
      charCode !== 9 &&
      charCode !== 46
    ) {
      e.preventDefault();
    }
  };

  const handleBSBInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      (charCode < 48 || charCode > 57) &&
      charCode !== 8 &&
      charCode !== 9 &&
      charCode !== 46 &&
      charCode !== 189 && // hyphen key
      charCode !== 109 // hyphen key (numpad)
    ) {
      e.preventDefault();
    }
  };

  const isFormValid = () => {
    // Required fields (excluding Mobile and Postcode which are now optional)
    const requiredFields = [
      "AccountName",
      "AccountBSB",
      "AccountNumber",
      "FirstName",
      "LastName",
      "Address",
      "Suburb",
      "Email",
      "State"
    ];

    // Check if all required fields have values
    const allRequiredFieldsFilled = requiredFields.every(
      (field) => formData[field as keyof DebitCardFormData].trim() !== ""
    );

    // Check if there are no validation errors
    const noValidationErrors = Object.values(validationErrors).every(
      (error) => !error
    );

    return allRequiredFieldsFilled && noValidationErrors;
  };

  const handleSubmit = async () => {
    // Validate all fields
    const isAccountNameValid = validateField(
      "AccountName",
      formData.AccountName
    );
    const isAccountBSBValid = validateField("AccountBSB", formData.AccountBSB);
    const isAccountNumberValid = validateField(
      "AccountNumber",
      formData.AccountNumber
    );
    const isFirstNameValid = validateField("FirstName", formData.FirstName);
    const isLastNameValid = validateField("LastName", formData.LastName);
    const isAddressValid = validateField("Address", formData.Address);
    const isSuburbValid = validateField("Suburb", formData.Suburb);
    const isPostcodeValid = validateField("Postcode", formData.Postcode);
    const isEmailValid = validateField("Email", formData.Email);
    const isMobileValid = validateField("Mobile", formData.Mobile);
    const isStateValid = validateField("State", formData.State);

    if (
      !isAccountNameValid ||
      !isAccountBSBValid ||
      !isAccountNumberValid ||
      !isFirstNameValid ||
      !isLastNameValid ||
      !isAddressValid ||
      !isSuburbValid ||
      !isEmailValid ||
      !isStateValid ||
      !isPostcodeValid ||
      !isMobileValid
    ) {
      setErrorMessage("Please fix the validation errors before submitting.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    // Validate required data
    if (!businessData?.BusinessId) {
      setErrorMessage("Business information not found. Please log in again.");
      setIsLoading(false);
      return;
    }

    if (!businessToken) {
      setErrorMessage("Authentication token not found. Please log in again.");
      setIsLoading(false);
      return;
    }

    try {
      // Call API to save the direct debit details
      const apiData = {
        // XeroCustomerId: paymentDetails?.XeroCustomerId
        //   ? paymentDetails?.XeroCustomerId
        //   : "",
        // MerchantExternalReferenceId:
        //   paymentDetails?.MerchantExternalReferenceSource?.toLowerCase() ===
        //   "ezidebit"
        //     ? paymentDetails?.Id
        //     : 0,
        AccountName: formData.AccountName.trim(),
        AccountNumber: formData.AccountNumber.trim(),
        AccountBSB: formData.AccountBSB.trim().replace(/-/g, ""), // Remove hyphens before sending
        FirstName: formData.FirstName.trim(),
        LastName: formData.LastName.trim(),
        Address: formData.Address.trim(),
        Suburb: formData.Suburb.trim(),
        Postcode: formData.Postcode.trim(),
        Email: formData.Email.trim(),
        Mobile: formData.Mobile.trim(),
        State: formData.State.trim()
      };

      const config = {
        method: "POST",
        url: `${process.env.REACT_APP_API_BASE_URL}/billingplatform-business/billingdetails/${businessData.BusinessId}`,
        headers: {
          Authorization: `Bearer ${businessToken}`,
          "Content-Type": "application/json"
        },
        data: apiData
      };

      console.log("Calling API with data:", apiData);
      console.log("API URL:", config.url);

      const response = await axios.request(config);
      console.log("API Response:", response.data);

      // Show success message and redirect after delay
      setIsSuccess(true);
      setSuccessMessage("Direct debit details added successfully!");

      // Auto redirect after 2 seconds
      setTimeout(() => {
        navigate(returnUrl);
      }, 2000);
    } catch (error: any) {
      console.error("Error saving direct debit details:", error);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(returnUrl);
  };

  const renderContent = () => {
    if (isInitialLoading) {
      return (
        <Box sx={{ mt: 2, textAlign: "center", py: 4 }}>
          <Loader />
          <Typography variant="body1" color="text.secondary">
            Loading existing payment details...
          </Typography>
        </Box>
      );
    }

    if (isSuccess) {
      return (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <CheckCircleIcon
            sx={{ fontSize: 64, color: "success.main", mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            Direct Debit Added Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your bank account details have been securely added to your account.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting you back to payment details in a few seconds...
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Enter your bank account information for direct debit payments. Your
          details are encrypted and processed securely.
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        <Box component="form" sx={{ width: "100%" }}>
          <Grid container spacing={3}>
            {/* Account Name Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.AccountName}
                onChange={(e) =>
                  handleInputChange("AccountName", e.target.value)
                }
                variant="outlined"
                disabled={isLoading || isInitialLoading}
                required
                error={!!validationErrors.AccountName}
                helperText={validationErrors.AccountName}
                placeholder="John Doe"
              />
            </Grid>

            {/* BSB Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="BSB"
                value={formData.AccountBSB}
                onChange={(e) =>
                  handleInputChange("AccountBSB", e.target.value)
                }
                onKeyPress={handleBSBInput}
                variant="outlined"
                disabled={isLoading || isInitialLoading}
                required
                error={!!validationErrors.AccountBSB}
                helperText={
                  validationErrors.AccountBSB ||
                  "6-digit BSB number (format: 123-456 or 123456)"
                }
                placeholder="123-456"
                inputProps={{ maxLength: 7 }}
              />
            </Grid>

            {/* Account Number Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Account Number"
                value={formData.AccountNumber}
                onChange={(e) =>
                  handleInputChange("AccountNumber", e.target.value)
                }
                onKeyPress={handleNumericInput}
                variant="outlined"
                disabled={isLoading || isInitialLoading}
                required
                error={!!validationErrors.AccountNumber}
                helperText={
                  validationErrors.AccountNumber || "6-10 digit account number"
                }
                placeholder="12345678"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            {/* First Name Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.FirstName}
                onChange={(e) => handleInputChange("FirstName", e.target.value)}
                variant="outlined"
                disabled={isLoading || isInitialLoading}
                required
                error={!!validationErrors.FirstName}
                helperText={validationErrors.FirstName}
                placeholder="John"
              />
            </Grid>

            {/* Last Name Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.LastName}
                onChange={(e) => handleInputChange("LastName", e.target.value)}
                variant="outlined"
                disabled={isLoading || isInitialLoading}
                required
                error={!!validationErrors.LastName}
                helperText={validationErrors.LastName}
                placeholder="Doe"
              />
            </Grid>

            {/* Address Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Address"
                value={formData.Address}
                onChange={(e) => handleInputChange("Address", e.target.value)}
                variant="outlined"
                disabled={true}
                required
                error={!!validationErrors.Address}
                helperText={validationErrors.Address}
                placeholder="123 Main Street"
              />
            </Grid>

            {/* Suburb Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Suburb"
                value={formData.Suburb}
                onChange={(e) => handleInputChange("Suburb", e.target.value)}
                variant="outlined"
                disabled={true}
                required
                error={!!validationErrors.Suburb}
                helperText={validationErrors.Suburb}
                placeholder="Melbourne"
              />
            </Grid>

            {/* Postcode Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Postcode"
                value={formData.Postcode}
                onChange={(e) => handleInputChange("Postcode", e.target.value)}
                onKeyPress={handleNumericInput}
                variant="outlined"
                disabled={true}
                error={!!validationErrors.Postcode}
                helperText={
                  validationErrors.Postcode || "4-digit postcode (optional)"
                }
                placeholder="3000"
                inputProps={{ maxLength: 4 }}
              />
            </Grid>

            {/* State Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.State}
                onChange={(e) => handleInputChange("State", e.target.value)}
                variant="outlined"
                disabled={true}
                required
                error={!!validationErrors.State}
                helperText={validationErrors.State}
                placeholder="VIC"
              />
            </Grid>

            {/* Email Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.Email}
                onChange={(e) => handleInputChange("Email", e.target.value)}
                variant="outlined"
                disabled={true}
                required
                error={!!validationErrors.Email}
                helperText={validationErrors.Email}
                placeholder="john.doe@example.com"
              />
            </Grid>

            {/* Mobile Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mobile"
                value={formData.Mobile}
                onChange={(e) => handleInputChange("Mobile", e.target.value)}
                variant="outlined"
                disabled={true}
                error={!!validationErrors.Mobile}
                helperText={
                  validationErrors.Mobile ||
                  "10-15 digit mobile number (optional)"
                }
                placeholder="0412 345 678"
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}
          >
            <CustomButton
              size="small"
              variant="outlined"
              className="blue_button"
              onClick={handleCancel}
              disabled={isLoading || isInitialLoading}
            >
              Cancel
            </CustomButton>

            <CustomButton
              size="small"
              variant="outlined"
              className="blue_button"
              onClick={handleSubmit}
              disabled={!isFormValid() || isLoading || isInitialLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} />
              ) : businessData?.MerchantExternalReferenceSource?.toLowerCase() ===
                "ezidebit" ? (
                "Update Direct Debit"
              ) : (
                "Add Direct Debit"
              )}
            </CustomButton>
          </Box>
        </Box>
      </Box>
    );
  };

  return <>{renderContent()}</>;
};

export default DebitCard;
